// src/pages/LoginPage.jsx — Authentication page with horizontal background image
import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Sparkles, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import loginBg from "@/assets/images/Loginbg_landscape.jpeg";
import logoImg from "@/assets/images/logo.jpg";

const DEMO_ACCOUNTS = [
  { label: "Admin", email: "admin@ctems.in", password: "password123", role: "admin" },
  { label: "Manager", email: "manager@ctems.in", password: "password123", role: "manager" },
  { label: "Employee", email: "priya@ctems.in", password: "password123", role: "employee" },
  { label: "Finance", email: "finance@ctems.in", password: "password123", role: "finance" },
  { label: "Travel Desk", email: "desk@ctems.in", password: "password123", role: "travel_desk" },
];

export default function LoginPage() {
  const { login, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  if (isAuthenticated) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedEmail = form.email.trim();
    if (!trimmedEmail && !form.password) {
      const msg = "Please enter your email address and password.";
      setErrorMessage(msg);
      toast.error(msg);
      return;
    }

    if (!trimmedEmail) {
      const msg = "Please enter your email address.";
      setErrorMessage(msg);
      toast.error(msg);
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      const msg = "Please enter a valid email address.";
      setErrorMessage(msg);
      toast.error(msg);
      return;
    }

    if (!form.password) {
      const msg = "Please enter your password.";
      setErrorMessage(msg);
      toast.error(msg);
      return;
    }

    try {
      setErrorMessage("");
      await login(trimmedEmail, form.password);
      toast.success("Welcome back!");
      navigate("/app/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = "Invalid email or password. Please try again.";

      if (typeof detail === "string") {
        msg = detail;
      } else if (err.response?.status === 401) {
        msg = "Incorrect email or password. Please check your credentials.";
      } else if (err.response?.status === 422) {
        msg = "Please verify your email format and password.";
      } else if (!err.response) {
        msg = "Cannot connect to server. Please verify the backend is running.";
      }

      setErrorMessage(msg);
      toast.error(msg);
    }
  };

  const quickLogin = async (account) => {
    try {
      setErrorMessage("");
      setForm({ email: account.email, password: account.password });
      await login(account.email, account.password);
      toast.success(`Signed in as ${account.label}`);
      navigate("/app/dashboard");
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        "Quick login failed. Please check backend server.";
      setErrorMessage(msg);
      toast.error(msg);
    }
  };

  const inputCls =
    "w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:border-transparent bg-white/90 focus:bg-white transition-all";

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 bg-slate-900 overflow-hidden">
      {/* Background image with horizontal fit */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${loginBg})` }}
      />

      {/* Subtle dark tint so the image is sharp, vibrant and readable */}
      <div className="absolute inset-0 bg-slate-950/40" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative z-10 w-full max-w-sm sm:max-w-md"
      >
        {/* Logo Header */}
        <div className="text-center mb-6">
          <img
            src={logoImg}
            alt="CTEMS Logo"
            className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-2xl object-contain mb-3 inline-block drop-shadow-md"
          />
          <p className="text-slate-300 text-xs sm:text-sm font-medium mt-1">
            Corporate Travel & Expense Management
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-4 sm:p-5 border border-white/20">
          <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-4">
            Sign in to your account
          </h2>

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div>
              <label className="block text-[11px] font-bold text-slate-600 mb-1.5 uppercase tracking-wider">
                Email Address
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => {
                  setForm((f) => ({ ...f, email: e.target.value }));
                  if (errorMessage) setErrorMessage("");
                }}
                placeholder="you@company.com"
                className={`${inputCls} ${
                  errorMessage
                    ? "border-red-400 focus:ring-red-400"
                    : "border-slate-300 focus:ring-blue-500"
                }`}
              />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-slate-600 mb-1.5 uppercase tracking-wider">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, password: e.target.value }));
                    if (errorMessage) setErrorMessage("");
                  }}
                  placeholder="••••••••"
                  className={`${inputCls} pr-10 ${
                    errorMessage
                      ? "border-red-400 focus:ring-red-400"
                      : "border-slate-300 focus:ring-blue-500"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 sm:py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm shadow-md transition-all disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          {/* Quick Demo Accounts Switcher */}
          <div className="mt-6 pt-5 border-t border-slate-200/80">
            <div className="flex items-center gap-1.5 mb-2.5">
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                Quick Demo Login
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {DEMO_ACCOUNTS.map((acc) => (
                <button
                  key={acc.role}
                  type="button"
                  onClick={() => quickLogin(acc)}
                  className="px-2.5 py-1 rounded-md bg-slate-100 hover:bg-blue-50 text-slate-700 hover:text-blue-700 border border-slate-200 text-xs font-semibold transition-all"
                >
                  {acc.label}
                </button>
              ))}
            </div>
          </div>

          <p className="mt-5 text-center text-xs text-slate-500">
            Don't have an account?{" "}
            <Link
              to="/landing"
              className="text-blue-600 hover:underline font-bold"
            >
              Request a demo
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}

